import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Heart } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      {/* Header */}
      <div className="glass-header border-b border-stone-200 sticky top-0 z-50">
        <div className="content-container">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Heart className="h-7 w-7 text-[#6B8E7B]" fill="#6B8E7B" />
              <div>
                <span className="text-xl font-bold font-['Outfit'] text-stone-800">NurseCommunity</span>
                <span className="text-xs text-stone-400 block">by Gina Rena</span>
              </div>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="rounded-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="content-container py-12">
        <Card className="max-w-3xl mx-auto border-stone-200 rounded-2xl">
          <CardContent className="p-8 md:p-12">
            <h1 className="text-3xl font-bold text-stone-800 font-['Outfit'] mb-2">Privacy Policy</h1>
            <p className="text-stone-500 mb-8">Last updated: March 29, 2026</p>

            <div className="prose prose-stone max-w-none space-y-6">
              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">1. Introduction</h2>
                <p className="text-stone-600 leading-relaxed">
                  Welcome to NurseCommunity by Gina Rena ("we," "our," or "us"). We are committed to protecting your privacy 
                  and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                  information when you use our platform at ginarena.com and our NurseCommunity application.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">2. Information We Collect</h2>
                <p className="text-stone-600 leading-relaxed mb-3">We collect information you provide directly to us:</p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li><strong>Account Information:</strong> Name, email address, password, professional specialization, years of experience, and bio.</li>
                  <li><strong>Profile Information:</strong> Profile photos and any other information you choose to add.</li>
                  <li><strong>Content:</strong> Posts, comments, messages, and anonymous submissions you create.</li>
                  <li><strong>Payment Information:</strong> Processed securely through Stripe. We do not store your full credit card details.</li>
                  <li><strong>Communications:</strong> Messages you send to other users through our platform.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">3. How We Use Your Information</h2>
                <p className="text-stone-600 leading-relaxed mb-3">We use the information we collect to:</p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process transactions and send related information</li>
                  <li>Send you technical notices, updates, and support messages</li>
                  <li>Respond to your comments, questions, and requests</li>
                  <li>Monitor and analyze trends, usage, and activities</li>
                  <li>Detect, investigate, and prevent fraudulent transactions and abuse</li>
                  <li>Personalize and improve your experience</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">4. Anonymous Posting</h2>
                <p className="text-stone-600 leading-relaxed">
                  Our premium "Anonymous Worries" feature allows you to share content without revealing your identity to 
                  other users. While your identity is hidden from the community, we retain internal records for moderation 
                  and legal compliance purposes. We will never publicly disclose the identity behind anonymous posts unless 
                  required by law.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">5. Information Sharing</h2>
                <p className="text-stone-600 leading-relaxed mb-3">We do not sell your personal information. We may share your information:</p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>With your consent or at your direction</li>
                  <li>With service providers who assist in our operations (e.g., Stripe for payments, Resend for emails)</li>
                  <li>To comply with legal obligations</li>
                  <li>To protect the rights, privacy, safety, or property of our users and the public</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">6. Data Security</h2>
                <p className="text-stone-600 leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal information, 
                  including encryption, secure servers, and access controls. However, no method of transmission over the 
                  Internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">7. Your Rights</h2>
                <p className="text-stone-600 leading-relaxed mb-3">You have the right to:</p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>Access and receive a copy of your personal data</li>
                  <li>Rectify or update your personal information</li>
                  <li>Delete your account and associated data</li>
                  <li>Object to or restrict processing of your data</li>
                  <li>Withdraw consent at any time</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">8. Cookies</h2>
                <p className="text-stone-600 leading-relaxed">
                  We use essential cookies to maintain your session and authentication. These are necessary for the 
                  platform to function properly. We do not use tracking or advertising cookies.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">9. Children's Privacy</h2>
                <p className="text-stone-600 leading-relaxed">
                  Our service is intended for nursing professionals and is not directed to individuals under 18 years of age. 
                  We do not knowingly collect personal information from children.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">10. Changes to This Policy</h2>
                <p className="text-stone-600 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the 
                  new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">11. Contact Us</h2>
                <p className="text-stone-600 leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact us at:
                </p>
                <div className="mt-3 p-4 bg-stone-50 rounded-xl">
                  <p className="text-stone-700 font-medium">Gina Rena</p>
                  <p className="text-stone-600">Email: info@ginarena.com</p>
                  <p className="text-stone-600">Website: ginarena.com</p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white py-8">
        <div className="content-container text-center text-stone-500 text-sm">
          <p>© 2026 NurseCommunity by Gina Rena. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/privacy" className="hover:text-[#6B8E7B]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#6B8E7B]">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
